import type { IAiModel, ModelSettings } from 'src/types/base';
import { THINKING_EFFORT } from 'src/types/enum';

const THINKING_EFFORT_ORDER: THINKING_EFFORT[] = Object.values(THINKING_EFFORT);

const PREFERRED_DEFAULT_THINKING_EFFORT = THINKING_EFFORT.MEDIUM;

export const normalizeThinkingEfforts = (raw: unknown): THINKING_EFFORT[] =>
  Array.isArray(raw)
    ? THINKING_EFFORT_ORDER.filter(
        (level) => level !== THINKING_EFFORT.OFF && raw.includes(level)
      )
    : [];

export const findConfiguredModel = (
  configuredModels?: IAiModel[],
  modelId?: number
): IAiModel | undefined =>
  configuredModels?.find((model) => model.id === modelId);

export const resolveDefaultThinkingEffort = (
  levels: THINKING_EFFORT[]
): THINKING_EFFORT => {
  if (!levels.length) {
    return THINKING_EFFORT.OFF;
  }

  const preferredRank = THINKING_EFFORT_ORDER.indexOf(
    PREFERRED_DEFAULT_THINKING_EFFORT
  );
  const distanceFromPreferred = (level: THINKING_EFFORT) =>
    Math.abs(THINKING_EFFORT_ORDER.indexOf(level) - preferredRank);

  return levels.reduce((closest, level) =>
    distanceFromPreferred(level) < distanceFromPreferred(closest)
      ? level
      : closest
  );
};

export const resolveThinkingEffortForModel = (
  configuredModel?: IAiModel,
  modelSettings?: ModelSettings
): THINKING_EFFORT | undefined => {
  const levels = configuredModel?.thinkingEfforts ?? [];

  if (!configuredModel || !levels.length) {
    return undefined;
  }

  const storedEffort = modelSettings?.[configuredModel.id]?.effort;

  return storedEffort &&
    (storedEffort === THINKING_EFFORT.OFF || levels.includes(storedEffort))
    ? storedEffort
    : resolveDefaultThinkingEffort(levels);
};

export const resolveThinkingEffortForRequest = (
  configuredModels?: IAiModel[],
  modelSettings?: ModelSettings,
  modelId?: number | unknown
): THINKING_EFFORT | undefined => {
  const effort = resolveThinkingEffortForModel(
    findConfiguredModel(configuredModels, Number(modelId)),
    modelSettings
  );

  return effort === THINKING_EFFORT.OFF ? undefined : effort;
};
